import { useState } from 'react'
import { Smartphone, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useInstallPrompt } from './useInstallPrompt'
import { InstallSteps } from './InstallSteps'
import { InstallTexts, resolveTexts, satzanfang } from './install.types'

/**
 * AP-17 (Baustein D), seit AP-17-E geteilt von ERP und Stempel-PWA.
 *
 * Permanente Installationsanleitung fürs Profil — aufklappbar, zeigt je nach
 * Lage:
 *  - „Installiert", wenn die App im Standalone-Modus läuft
 *  - „Jetzt installieren", wenn der Browser den nativen Ein-Klick anbietet
 *    (Chrome/Edge/Android)
 *  - die plattformabhängige Schritt-für-Schritt-Anleitung als Fallback
 */
export function InstallGuide({
  texts,
  className,
}: {
  texts: InstallTexts
  className?: string
}) {
  const { canInstall, install, isInstalled, platform } = useInstallPrompt()
  const [open, setOpen] = useState(false)
  const { appName, installLabel } = resolveTexts(texts)

  return (
    <div className={cn('overflow-hidden rounded-xl border bg-card shadow-sm', className)}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3.5 text-left transition-colors hover:bg-accent/50"
      >
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10">
            <Smartphone size={16} className="text-primary" />
          </div>
          <div>
            <span className="text-sm font-medium text-foreground">{installLabel}</span>
            {isInstalled && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                <CheckCircle2 size={12} /> Installiert
              </span>
            )}
          </div>
        </div>
        {open ? (
          <ChevronUp size={16} className="shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown size={16} className="shrink-0 text-muted-foreground" />
        )}
      </button>

      {/* Inhalt */}
      {open && (
        <div className="space-y-4 border-t px-4 pb-4 pt-1">
          {isInstalled ? (
            <div className="flex items-center gap-2 rounded-lg border border-green-500/20 bg-green-500/10 px-3 py-2.5">
              <CheckCircle2 size={16} className="shrink-0 text-green-600 dark:text-green-400" />
              <p className="text-sm text-green-700 dark:text-green-300">
                {satzanfang(appName)} ist bereits auf diesem Gerät installiert und läuft im App-Modus.
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Installiere {appName} für schnellen Zugriff direkt vom Startbildschirm bzw.
                Desktop – auch ohne den Browser zu öffnen.
              </p>

              {/* Chrome/Edge/Android: nativer Ein-Klick */}
              {canInstall && (
                <button
                  onClick={install}
                  className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.98]"
                >
                  Jetzt installieren
                </button>
              )}

              <div className="space-y-3">
                {canInstall && (
                  <p className="text-xs text-muted-foreground">Oder manuell über den Browser:</p>
                )}
                <InstallSteps platform={platform} texts={texts} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
